import { useEffect, useRef } from 'react';
import { useMoveMissionToCampaign, tryOptimisticMoveMissionToCampaign } from '../../../hooks/campaignNesting/useMoveMissionToCampaign';
import { useNestCampaign } from '../../../hooks/campaignNesting/useNestCampaign';
import { setMissionToSidebarMoveHandler, getMissionToSidebarState } from '../../../utils/missionToSidebarBridge';

/** Would nesting `nestedSprintId` under `targetSprintId` create a cycle? */
const wouldCreateCircularNest = (sprints, nestedSprintId, targetSprintId) => {
  const byId = new Map((sprints || []).map((s) => [s.id, s]));
  let current = byId.get(targetSprintId);
  let hops = 0;
  while (current && hops < 100) {
    if (current.id === nestedSprintId) return true;
    current = current.parentSprintId ? byId.get(current.parentSprintId) : null;
    hops += 1;
  }
  return false;
};

/**
 * Wires drag-from-flowchart → sidebar campaign drop through the global bridge.
 */
export function useMissionToSidebarBridge(setSprints, cancelPendingSaveForSprint, sprints) {
  const { moveMissionToCampaign } = useMoveMissionToCampaign(setSprints, cancelPendingSaveForSprint);
  const { nestCampaign } = useNestCampaign(setSprints);
  const moveMissionRef = useRef(moveMissionToCampaign);
  const nestCampaignRef = useRef(nestCampaign);
  const sprintsRef = useRef(sprints);
  const setSprintsRef = useRef(setSprints);
  moveMissionRef.current = moveMissionToCampaign;
  nestCampaignRef.current = nestCampaign;
  sprintsRef.current = sprints;
  setSprintsRef.current = setSprints;

  useEffect(() => {
    setMissionToSidebarMoveHandler((payload) => {
      const currentSprints = sprintsRef.current;

      // Container node (mission with a nested campaign inside): re-nest the campaign
      // under the drop target — same operation as dragging a campaign onto a campaign
      // in the sidebar. The link task and everything inside move with it.
      if (payload.nestedSprintId) {
        if (wouldCreateCircularNest(currentSprints, payload.nestedSprintId, payload.targetSprintId)) {
          console.warn('[M2S] container drop REFUSED — would create circular nesting', payload);
          return false;
        }
        console.log('[M2S] container drop — re-nesting campaign under target', payload);
        nestCampaignRef.current(
          payload.nestedSprintId,
          payload.targetSprintId,
          payload.taskName,
          { oldParentSprintId: payload.sourceSprintId },
        ).then(() => {
          console.log('[M2S] container re-nest complete', payload);
        }).catch((err) => {
          console.error('[M2S] container re-nest FAILED', payload, err);
        });
        return true;
      }
      if (tryOptimisticMoveMissionToCampaign(setSprintsRef.current, payload, currentSprints)) {
        console.log('[M2S] optimistic move applied, persisting in background', payload);
        moveMissionRef.current(payload, currentSprints).then((ok) => {
          console.log('[M2S] background persist result:', ok, payload);
        });
        return true;
      }

      const bridge = getMissionToSidebarState();
      const hasTaskSnapshot =
        bridge.sourceTask?.id === payload.taskId
        && bridge.sourceSprintId === payload.sourceSprintId;

      console.warn('[M2S] optimistic move FAILED, falling back', {
        payload,
        hasTaskSnapshot,
        sprintsCount: currentSprints?.length,
        targetInSprints: !!currentSprints?.find((s) => s.id === payload.targetSprintId),
        sourceInSprints: !!currentSprints?.find((s) => s.id === payload.sourceSprintId),
      });

      if (hasTaskSnapshot) {
        moveMissionRef.current(payload, currentSprints).then((ok) => {
          console.log('[M2S] fallback (snapshot) move result:', ok, payload);
        });
        return true;
      }

      console.warn('[M2S] move handler returning FALSE — no snapshot, drop will cancel');
      return false;
    });
  });
  // ^ No dependency array on purpose: re-register after EVERY render. The handler only
  // reads refs so re-registering is free, and it guarantees the bridge always has a
  // live handler even after hot reloads re-evaluate the bridge module.
}
